import { AccountService, type ListAccountsResponse } from '@qdrant/qdrant-cloud-public-api/account/v1';
import {
  createAccount,
  deleteAccount,
  deleteAccountMember,
  getAccount,
  getAccountMember,
  leaveAccount,
  listAccountInvites,
  listAccountMembers,
  listAccounts,
  listReceivedAccountInvites,
  suggestCompanies,
  updateAccount,
} from '@qdrant/qdrant-cloud-public-api/account/v1/connect-query';
import { type GetAuthenticatedUserResponseValid } from '@qdrant/qdrant-cloud-public-api/iam/v1';
import { getAuthenticatedUser } from '@qdrant/qdrant-cloud-public-api/iam/v1/connect-query';
import { listUsersWithRoles } from '@qdrant/qdrant-cloud-public-api/ui/v1/ui/connect-query';
import { redirect } from '@tanstack/react-router';
import { resolveFallbackAccount } from '@/api/services/helpers';
import { updateUserMutationFn } from '@/api/services/iam';
import { transport } from '@/api/transport';
import {
  createConnectQueryKey,
  createServiceQueries,
  type MutationFactory,
  queryOptions,
  useMutation,
} from '@/connect-query';
import { accountIdUrlParam } from '@/routes/_authenticated/_dashboard/accounts/$accountId/route.hooks';

export const accountService = createServiceQueries(AccountService, {
  getAccountQuery: (params) => queryOptions(getAccount, params, { transport, select: (d) => d.account }),
  listAccountsQuery: () => queryOptions(listAccounts, undefined, { transport, select: (d) => d.items }),
  listAccountInvitesQuery: (params) => queryOptions(listAccountInvites, params, { transport, select: (d) => d.items }),
  listReceivedAccountInvitesQuery: () =>
    queryOptions(listReceivedAccountInvites, undefined, { transport, select: (d) => d.items }),
  suggestCompaniesQuery: (params) => queryOptions(suggestCompanies, params, { transport, select: (d) => d.items }),
});

export const useCreateAccountMutation: MutationFactory<typeof createAccount, { updateUserDefaultAccount: boolean }> = (
  options,
) =>
  useMutation(createAccount, {
    onSuccess: async (data, _vars, { router, updateUserDefaultAccount }, { client }) => {
      const listAccountsQueryKey = createConnectQueryKey({ schema: listAccounts });
      // Update the accounts list in the cache to include the newly created account
      client.setQueryData<ListAccountsResponse>(listAccountsQueryKey, (input) =>
        input ? { ...input, items: [...input.items, data.account] } : input,
      );
      // Update the authenticated user's default account on demand
      if (updateUserDefaultAccount) {
        const authenticatedUserQueryKey = createConnectQueryKey({ schema: getAuthenticatedUser });
        const authenticatedUser = await client.ensureQueryData<GetAuthenticatedUserResponseValid>({
          queryKey: authenticatedUserQueryKey,
        });

        if (authenticatedUser) {
          // Update the user's default account to the newly created account
          const { user } = await updateUserMutationFn({
            user: authenticatedUser.user,
            defaultAccountId: data.account.id,
            client,
          });
          // Update the authenticated user data in the cache
          client.setQueryData(authenticatedUserQueryKey, (input) => (input ? { ...input, user } : input));
        }
      }
      // Remove account company suggestions from the cache
      client.removeQueries({
        queryKey: createConnectQueryKey({ schema: suggestCompanies }),
      });
      // Navigate to the newly created account's page
      return router.navigate({
        to: '/accounts/$accountId',
        params: { accountId: accountIdUrlParam.parse(data.account.id) },
      });
    },
    ...options,
  });

export const useDeleteAccountMutation: MutationFactory<typeof deleteAccount, { shouldRedirect?: boolean }> = (
  options,
) =>
  useMutation(deleteAccount, {
    /*
     * After deleting an account, we need to ensure that the user is redirected appropriately.
     * If there are other accounts available, we redirect the user to one of those accounts
     * (preferably their default or owned account). If no accounts remain, we redirect them
     * to the logout page to avoid any inconsistent state.
     *
     * Callers that delete several accounts in a loop (e.g. the user-deletion flow) pass
     * `redirect: false` via `onMutate` so cache invalidation still runs but the navigation is
     * suppressed — otherwise the first deletion would redirect away and abort the loop.
     */
    onSuccess: async (_data, { accountId }, { shouldRedirect = true }, { client }) => {
      // Remove the deleted account's data from the cache, will leave active subscribers with stale data.
      client.removeQueries({ queryKey: createConnectQueryKey({ schema: getAccount, input: { accountId } }) });
      client.removeQueries({ queryKey: createConnectQueryKey({ schema: listAccountInvites, input: { accountId } }) });
      client.removeQueries({ queryKey: createConnectQueryKey({ schema: listAccountMembers, input: { accountId } }) });
      client.removeQueries({ queryKey: createConnectQueryKey({ schema: listUsersWithRoles, input: { accountId } }) });

      // Invalidate the authenticated user and account's list to ensure the cache reflects
      // the deletion, but prevent active subscribers from triggering an immediate refetch.
      await Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: getAuthenticatedUser }),
          refetchType: 'none',
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: listAccounts }),
          refetchType: 'none', // Prevents active subscribers from triggering an immediate refetch
        }),
      ]);

      if (!shouldRedirect) {
        return;
      }

      const fallbackAccount = await resolveFallbackAccount({ queryClient: client, accountId });
      // If a fallback account exists, navigate to it
      if (fallbackAccount) {
        throw redirect({
          to: '/accounts/$accountId',
          params: { accountId: accountIdUrlParam.parse(fallbackAccount.id) },
          replace: true,
        });
      }
      throw redirect({ to: '/accounts', replace: true });
    },
    ...options,
  });

export const useUpdateAccountMutation: MutationFactory<typeof updateAccount> = () =>
  useMutation(updateAccount, {
    onSuccess: (data, _variables, _onMutateResult, { client }) => {
      const listAccountsQueryKey = createConnectQueryKey({ schema: listAccounts });
      // Update the accounts list in the cache to reflect the updated account details
      client.setQueryData(listAccountsQueryKey, (input) =>
        input
          ? {
              ...input,
              items: input.items.map((account) => (account.id === data.account.id ? data.account : account)),
            }
          : input,
      );
      const accountId = data.account.id;
      const listUsersWithRolesQueryKey = createConnectQueryKey({
        schema: listUsersWithRoles,
        input: { accountId },
      });

      return Promise.all([
        client.invalidateQueries({
          queryKey: listUsersWithRolesQueryKey,
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: getAccount, input: { accountId } }),
        }),
      ]);
    },
  });

export const useDeleteAccountMemberMutation: MutationFactory<typeof deleteAccountMember> = () =>
  useMutation(deleteAccountMember, {
    onSuccess: async (_data, { accountId, userId }, _onMutateResult, { client }) => {
      const listAccountMembersQueryKey = createConnectQueryKey({ schema: listAccountMembers, input: { accountId } });
      const listUsersWithRolesQueryKey = createConnectQueryKey({ schema: listUsersWithRoles, input: { accountId } });
      const getAccountMemberQueryKey = createConnectQueryKey({
        schema: getAccountMember,
        input: { accountId, userId },
      });

      return Promise.all([
        client.invalidateQueries({ queryKey: listAccountMembersQueryKey }),
        client.invalidateQueries({ queryKey: listUsersWithRolesQueryKey }),
        client.invalidateQueries({ queryKey: getAccountMemberQueryKey }),
      ]);
    },
  });

export const useLeaveAccountMutation: MutationFactory<typeof leaveAccount, { shouldRedirect?: boolean }> = (options) =>
  useMutation(leaveAccount, {
    // Like `useDeleteAccountMutation`, batch callers (user deletion) pass `shouldRedirect: false` so cache
    // invalidation runs without navigating away mid-loop.
    onSuccess: async (_data, { accountId }, { router, shouldRedirect = true }, { client }) => {
      await Promise.all([
        client.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getAuthenticatedUser }) }),
        client.invalidateQueries({ queryKey: createConnectQueryKey({ schema: listAccounts }) }),
      ]);

      if (!shouldRedirect) {
        return;
      }

      const fallbackAccount = await resolveFallbackAccount({ queryClient: client, accountId });
      // If a fallback account exists, navigate to it
      if (fallbackAccount) {
        // Invalidate the authenticated user and account's list and redirect to select the fallback account
        return router.navigate({
          to: '/accounts/$accountId',
          params: { accountId: accountIdUrlParam.parse(fallbackAccount.id) },
          replace: true,
        });
      }
      // Hard-redirect to prevent potential UI state changes after deletion.
      return router.navigate({ to: '/accounts', replace: true });
    },
    ...options,
  });
